import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  KeyRound,
  ListChecks,
  RefreshCw,
  ShieldCheck,
  Smartphone,
  TerminalSquare,
} from 'lucide-react';
import {
  approveEnrollmentDeviceAuth,
  listLocalEnrollments,
  getAuthSession,
  approveLocalEnrollment,
  approveLocalEnrollmentActivation,
  approveLocalEnrollmentSecretRelease,
  launchLocalEnrollmentDocker,
  fetchLocalEnrollmentAuthorityStatus,
  issueLocalEnrollmentToken,
  prepareLocalEnrollment,
  requestLocalEnrollmentAuthorityProvisioning,
  type LocalEnrollmentAuthorityState,
  type LocalEnrollmentDockerLaunchResult,
  type LocalEnrollmentDoctorCheckResult,
  reissueLocalEnrollmentToken,
  runLocalEnrollmentDoctorCheck,
  runLocalEnrollmentFederationCheck,
  type LocalEnrollmentFederationCheckResult,
  type LocalEnrollmentTokenResult,
  type PrepareLocalEnrollmentInput,
  type PrepareLocalEnrollmentResult,
} from '@/lib/api';
import { Button } from '@/components/ui/button';

export type LocalEnrollmentPendingAction =
  | 'prepare'
  | 'approve'
  | 'requestAuthority'
  | 'refreshAuthority'
  | 'approveSecretRelease'
  | 'approveActivation'
  | 'issue'
  | 'launchDocker'
  | 'doctorCheck'
  | 'federationCheck'
  | 'reissue'
  | null;

export interface LocalEnrollmentPageState {
  form: PrepareLocalEnrollmentInput;
  prepared: PrepareLocalEnrollmentResult | null;
  authority: LocalEnrollmentAuthorityState | null;
  issued: LocalEnrollmentTokenResult | null;
  launch: LocalEnrollmentDockerLaunchResult | null;
  doctorCheck: LocalEnrollmentDoctorCheckResult | null;
  federationCheck: LocalEnrollmentFederationCheckResult | null;
  statusMessage: string | null;
  errorMessage: string | null;
  pendingAction: LocalEnrollmentPendingAction;
}

export interface LocalEnrollmentPageApi {
  prepareLocalEnrollment: typeof prepareLocalEnrollment;
  approveLocalEnrollment: typeof approveLocalEnrollment;
  requestLocalEnrollmentAuthorityProvisioning: typeof requestLocalEnrollmentAuthorityProvisioning;
  fetchLocalEnrollmentAuthorityStatus: typeof fetchLocalEnrollmentAuthorityStatus;
  approveLocalEnrollmentSecretRelease: typeof approveLocalEnrollmentSecretRelease;
  approveLocalEnrollmentActivation: typeof approveLocalEnrollmentActivation;
  issueLocalEnrollmentToken: typeof issueLocalEnrollmentToken;
  launchLocalEnrollmentDocker: typeof launchLocalEnrollmentDocker;
  reissueLocalEnrollmentToken: typeof reissueLocalEnrollmentToken;
  runLocalEnrollmentDoctorCheck: typeof runLocalEnrollmentDoctorCheck;
  runLocalEnrollmentFederationCheck: typeof runLocalEnrollmentFederationCheck;
}

export const localEnrollmentPageApi: LocalEnrollmentPageApi = {
  prepareLocalEnrollment,
  approveLocalEnrollment,
  requestLocalEnrollmentAuthorityProvisioning,
  fetchLocalEnrollmentAuthorityStatus,
  approveLocalEnrollmentSecretRelease,
  approveLocalEnrollmentActivation,
  issueLocalEnrollmentToken,
  launchLocalEnrollmentDocker,
  reissueLocalEnrollmentToken,
  runLocalEnrollmentDoctorCheck,
  runLocalEnrollmentFederationCheck,
};

interface LocalEnrollmentViewProps {
  form: PrepareLocalEnrollmentInput;
  onFormChange: (field: keyof PrepareLocalEnrollmentInput, value: string | number) => void;
  onPrepare: () => void;
  onApprove: () => void;
  onRequestAuthorityProvisioning: () => void;
  onRefreshAuthorityStatus: () => void;
  onApproveSecretRelease: () => void;
  onApproveActivation: () => void;
  onIssue: () => void;
  onRunDoctorCheck: () => void;
  onRunFederationCheck: () => void;
  onLaunch: () => void;
  onReissue: () => void;
  prepared: PrepareLocalEnrollmentResult | null;
  authority: LocalEnrollmentAuthorityState | null;
  issued: LocalEnrollmentTokenResult | null;
  launch: LocalEnrollmentDockerLaunchResult | null;
  doctorCheck: LocalEnrollmentDoctorCheckResult | null;
  federationCheck: LocalEnrollmentFederationCheckResult | null;
  statusMessage: string | null;
  errorMessage: string | null;
  pendingAction:
    | 'prepare'
    | 'approve'
    | 'requestAuthority'
    | 'refreshAuthority'
    | 'approveSecretRelease'
    | 'approveActivation'
    | 'issue'
    | 'launchDocker'
    | 'doctorCheck'
    | 'federationCheck'
    | 'reissue'
    | null;
}

function fieldClassName(): string {
  return 'h-10 w-full rounded-md border border-border bg-surface px-3 text-sm text-text-primary';
}

export function createInitialLocalEnrollmentPageState(): LocalEnrollmentPageState {
  return {
    form: {
      deviceId: 'devbox-source',
      environment: 'source-dev',
      publicUrl: 'http://localhost:4000',
      backendPort: 4000,
      webPort: 5173,
    },
    prepared: null,
    authority: null,
    issued: null,
    launch: null,
    doctorCheck: null,
    federationCheck: null,
    statusMessage: null,
    errorMessage: null,
    pendingAction: null,
  };
}

function formatAuthorityStage(
  status:
    | LocalEnrollmentAuthorityState['authorityStatus']
    | LocalEnrollmentAuthorityState['releaseStatus']
    | LocalEnrollmentAuthorityState['activationApprovalStatus']
): string {
  return status.replace(/_/g, ' ');
}

function formatProviderAuthStatus(
  value: LocalEnrollmentDockerLaunchResult['readiness']['providerAuth'] | null | undefined
): string {
  if (!value) return 'unknown';
  if (!value.detail) return value.status ?? 'unknown';
  return `${value.status ?? 'unknown'} — ${value.detail}`;
}

function isAuthorityReady(authority: LocalEnrollmentAuthorityState | null): boolean {
  return (
    authority?.authorityStatus === 'ready' &&
    authority.releaseStatus === 'ready' &&
    authority.activationApprovalStatus === 'approved'
  );
}

export function getManifestApprovalSuccessMessage(enrollmentId: string): string {
  return `Approved ${enrollmentId}. Continue with MOS provisioning, refresh status, and final activation approval before issuing a token.`;
}

export function getAuthorityProvisioningSuccessMessage(enrollmentId: string): string {
  return `Requested MOS provisioning for ${enrollmentId}.`;
}

export function getAuthorityRefreshSuccessMessage(enrollmentId: string): string {
  return `Refreshed MOS authority status for ${enrollmentId}.`;
}

export function getSecretReleaseApprovalSuccessMessage(enrollmentId: string): string {
  return `Secret release approved for ${enrollmentId}. Activation approval is now available.`;
}

export function getActivationApprovalSuccessMessage(enrollmentId: string): string {
  return `Activation approved for ${enrollmentId}. Token issuance is now enabled.`;
}

export async function prepareLocalEnrollmentPageState(
  state: LocalEnrollmentPageState,
  api: LocalEnrollmentPageApi = localEnrollmentPageApi
): Promise<LocalEnrollmentPageState> {
  const result = await api.prepareLocalEnrollment(state.form);
  return {
    ...state,
    prepared: result,
    authority: null,
    issued: null,
    doctorCheck: null,
    federationCheck: null,
    statusMessage: `Prepared ${result.enrollment.id}. Review the manifest and approve it to continue.`,
    errorMessage: null,
    pendingAction: null,
  };
}

export async function approveLocalEnrollmentPageState(
  state: LocalEnrollmentPageState,
  api: LocalEnrollmentPageApi = localEnrollmentPageApi
): Promise<LocalEnrollmentPageState> {
  if (!state.prepared) return state;
  const result = await api.approveLocalEnrollment(
    state.prepared.enrollment.id,
    state.prepared.manifestHash
  );
  return {
    ...state,
    prepared: {
      ...state.prepared,
      enrollment: result.enrollment,
      manifestHash: result.enrollment.manifestHash,
    },
    statusMessage: getManifestApprovalSuccessMessage(result.enrollment.id),
    errorMessage: null,
    pendingAction: null,
  };
}

export async function requestAuthorityProvisioningPageState(
  state: LocalEnrollmentPageState,
  api: LocalEnrollmentPageApi = localEnrollmentPageApi
): Promise<LocalEnrollmentPageState> {
  if (!state.prepared) return state;
  const result = await api.requestLocalEnrollmentAuthorityProvisioning(
    state.prepared.enrollment.id
  );
  return {
    ...state,
    authority: result.authority,
    statusMessage: getAuthorityProvisioningSuccessMessage(result.authority.enrollmentId),
    errorMessage: null,
    pendingAction: null,
  };
}

export async function refreshAuthorityStatusPageState(
  state: LocalEnrollmentPageState,
  api: LocalEnrollmentPageApi = localEnrollmentPageApi
): Promise<LocalEnrollmentPageState> {
  if (!state.prepared) return state;
  const result = await api.fetchLocalEnrollmentAuthorityStatus(state.prepared.enrollment.id);
  return {
    ...state,
    authority: result.authority,
    statusMessage: getAuthorityRefreshSuccessMessage(result.authority.enrollmentId),
    errorMessage: null,
    pendingAction: null,
  };
}

export async function approveActivationPageState(
  state: LocalEnrollmentPageState,
  api: LocalEnrollmentPageApi = localEnrollmentPageApi
): Promise<LocalEnrollmentPageState> {
  if (!state.prepared) return state;
  const result = await api.approveLocalEnrollmentActivation(state.prepared.enrollment.id);
  return {
    ...state,
    authority: result.authority,
    statusMessage: getActivationApprovalSuccessMessage(result.authority.enrollmentId),
    errorMessage: null,
    pendingAction: null,
  };
}

export async function approveSecretReleasePageState(
  state: LocalEnrollmentPageState,
  api: LocalEnrollmentPageApi = localEnrollmentPageApi
): Promise<LocalEnrollmentPageState> {
  if (!state.prepared) return state;
  const result = await api.approveLocalEnrollmentSecretRelease(state.prepared.enrollment.id);
  return {
    ...state,
    authority: result.authority,
    statusMessage: getSecretReleaseApprovalSuccessMessage(result.authority.enrollmentId),
    errorMessage: null,
    pendingAction: null,
  };
}

export async function launchLocalEnrollmentDockerPageState(
  state: LocalEnrollmentPageState,
  api: LocalEnrollmentPageApi = localEnrollmentPageApi
): Promise<LocalEnrollmentPageState> {
  if (!state.prepared || !state.issued) return state;
  if (state.issued.enrollmentId !== state.prepared.enrollment.id) return state;

  const launch = await api.launchLocalEnrollmentDocker(state.prepared.enrollment.id, {
    token: state.issued.token,
  });

  return {
    ...state,
    launch,
    statusMessage: `Started local deployment for ${state.prepared.enrollment.id}.`,
    errorMessage: null,
    pendingAction: null,
  };
}

export async function runDoctorCheckPageState(
  state: LocalEnrollmentPageState,
  api: LocalEnrollmentPageApi = localEnrollmentPageApi
): Promise<LocalEnrollmentPageState> {
  if (!state.prepared) return state;

  const doctorCheck = await api.runLocalEnrollmentDoctorCheck(state.prepared.enrollment.id);

  return {
    ...state,
    doctorCheck,
    statusMessage: `Doctor check complete for ${doctorCheck.enrollmentId}.`,
    errorMessage: null,
    pendingAction: null,
  };
}

export async function runFederationCheckPageState(
  state: LocalEnrollmentPageState,
  api: LocalEnrollmentPageApi = localEnrollmentPageApi
): Promise<LocalEnrollmentPageState> {
  if (!state.prepared) return state;

  const federationCheck = await api.runLocalEnrollmentFederationCheck(state.prepared.enrollment.id);

  return {
    ...state,
    federationCheck,
    statusMessage: `Federation check complete for ${federationCheck.enrollmentId}.`,
    errorMessage: null,
    pendingAction: null,
  };
}

export function LocalEnrollmentView({
  form,
  onFormChange,
  onPrepare,
  onApprove,
  onRequestAuthorityProvisioning,
  onRefreshAuthorityStatus,
  onApproveSecretRelease,
  onApproveActivation,
  onIssue,
  onRunDoctorCheck,
  onRunFederationCheck,
  onLaunch,
  onReissue,
  prepared,
  authority,
  issued,
  launch,
  doctorCheck,
  federationCheck,
  statusMessage,
  errorMessage,
  pendingAction,
}: LocalEnrollmentViewProps): React.ReactElement {
  const canApprove = prepared?.enrollment.status === 'prepared';
  const manifestApproved =
    prepared?.enrollment.status === 'approved' || prepared?.enrollment.status === 'token_issued';
  const canRequestAuthorityProvisioning = manifestApproved;
  const canRefreshAuthorityStatus = authority?.mosAuthorityRequestId != null;
  const canApproveSecretRelease =
    manifestApproved &&
    authority?.authorityStatus === 'ready' &&
    authority.releaseStatus === 'pending';
  const canApproveActivation =
    manifestApproved &&
    authority?.authorityStatus === 'ready' &&
    authority.releaseStatus === 'ready' &&
    authority.activationApprovalStatus === 'pending';
  const canIssue = manifestApproved && isAuthorityReady(authority);
  const canLaunch = !!issued && issued.enrollmentId === prepared?.enrollment.id;
  const canRunDoctorCheck = !!prepared;
  const canRunFederationCheck = !!prepared;
  const isBusy = pendingAction !== null;

  return (
    <div className="flex h-full min-h-0 flex-col bg-background text-text-primary">
      <div className="border-b border-border px-6 py-4">
        <h1 className="text-xl font-semibold">Local enrollment</h1>
        <p className="mt-1 text-sm text-text-secondary">
          Prepare a workstation bootstrap manifest, approve it, then issue or reissue the one-shot
          command the target machine will run.
        </p>
      </div>

      <div className="grid flex-1 gap-6 overflow-y-auto px-6 py-6 lg:grid-cols-[minmax(360px,420px)_1fr]">
        <section className="space-y-4">
          <div className="rounded-xl border border-border bg-surface p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-medium">
              <ShieldCheck className="h-4 w-4 text-primary" />
              Enrollment target
            </div>
            <div className="grid gap-3">
              <label className="grid gap-1 text-sm">
                <span className="text-text-secondary">Device ID</span>
                <input
                  data-testid="enrollment-device-id"
                  className={fieldClassName()}
                  value={form.deviceId}
                  onChange={event => {
                    onFormChange('deviceId', event.target.value);
                  }}
                />
              </label>
              <label className="grid gap-1 text-sm">
                <span className="text-text-secondary">Environment</span>
                <input
                  data-testid="enrollment-environment"
                  className={fieldClassName()}
                  value={form.environment}
                  onChange={event => {
                    onFormChange('environment', event.target.value);
                  }}
                />
              </label>
              <label className="grid gap-1 text-sm">
                <span className="text-text-secondary">Public URL</span>
                <input
                  data-testid="enrollment-public-url"
                  className={fieldClassName()}
                  value={form.publicUrl}
                  onChange={event => {
                    onFormChange('publicUrl', event.target.value);
                  }}
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="grid gap-1 text-sm">
                  <span className="text-text-secondary">Backend port</span>
                  <input
                    data-testid="enrollment-backend-port"
                    className={fieldClassName()}
                    inputMode="numeric"
                    value={String(form.backendPort)}
                    onChange={event => {
                      onFormChange('backendPort', Number(event.target.value || '0'));
                    }}
                  />
                </label>
                <label className="grid gap-1 text-sm">
                  <span className="text-text-secondary">Web port</span>
                  <input
                    data-testid="enrollment-web-port"
                    className={fieldClassName()}
                    inputMode="numeric"
                    value={String(form.webPort)}
                    onChange={event => {
                      onFormChange('webPort', Number(event.target.value || '0'));
                    }}
                  />
                </label>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button data-testid="enrollment-prepare" onClick={onPrepare} disabled={isBusy}>
                {pendingAction === 'prepare' ? 'Preparing…' : 'Prepare enrollment'}
              </Button>
              <Button
                data-testid="enrollment-approve"
                variant="outline"
                onClick={onApprove}
                disabled={!canApprove || isBusy}
              >
                {pendingAction === 'approve' ? 'Approving…' : 'Approve manifest'}
              </Button>
              <Button
                data-testid="enrollment-issue-token"
                variant="outline"
                onClick={onIssue}
                disabled={!canIssue || isBusy}
              >
                {pendingAction === 'issue' ? 'Issuing…' : 'Issue token'}
              </Button>
              <Button
                data-testid="enrollment-reissue-token"
                variant="ghost"
                onClick={onReissue}
                disabled={!issued || isBusy}
              >
                <RefreshCw className="h-4 w-4" />
                {pendingAction === 'reissue' ? 'Reissuing…' : 'Reissue token'}
              </Button>
              <Button
                data-testid="enrollment-launch-docker"
                variant="outline"
                onClick={onLaunch}
                disabled={!canLaunch || isBusy}
              >
                {pendingAction === 'launchDocker' ? 'Launching…' : 'Start local deployment'}
              </Button>
            </div>
            {statusMessage ? (
              <p data-testid="enrollment-status-message" className="mt-3 text-sm text-success">
                {statusMessage}
              </p>
            ) : null}
            {errorMessage ? (
              <p data-testid="enrollment-error-message" className="mt-3 text-sm text-error">
                {errorMessage}
              </p>
            ) : null}
          </div>

          <div className="rounded-xl border border-border bg-surface p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium">
              <ShieldCheck className="h-4 w-4 text-primary" />
              Authority request status
            </div>
            {prepared ? (
              <>
                <dl className="grid gap-2 text-sm">
                  <div>
                    <dt className="text-text-secondary">MOS request ID</dt>
                    <dd>{authority?.mosAuthorityRequestId ?? 'Not requested'}</dd>
                  </div>
                  <div>
                    <dt className="text-text-secondary">Client provisioning</dt>
                    <dd data-testid="enrollment-authority-status">
                      {formatAuthorityStage(authority?.authorityStatus ?? 'pending')}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-text-secondary">Secret release</dt>
                    <dd data-testid="enrollment-release-status">
                      {formatAuthorityStage(authority?.releaseStatus ?? 'pending')}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-text-secondary">Activation approval</dt>
                    <dd data-testid="enrollment-activation-status">
                      {formatAuthorityStage(authority?.activationApprovalStatus ?? 'pending')}
                    </dd>
                  </div>
                  {authority?.authorityClientId ? (
                    <div>
                      <dt className="text-text-secondary">Authority client ID</dt>
                      <dd className="break-all font-mono text-xs">{authority.authorityClientId}</dd>
                    </div>
                  ) : null}
                </dl>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button
                    data-testid="enrollment-request-authority"
                    variant="outline"
                    onClick={onRequestAuthorityProvisioning}
                    disabled={!canRequestAuthorityProvisioning || isBusy}
                  >
                    {pendingAction === 'requestAuthority'
                      ? 'Requesting…'
                      : 'Request MOS provisioning'}
                  </Button>
                  <Button
                    data-testid="enrollment-refresh-status"
                    variant="outline"
                    onClick={onRefreshAuthorityStatus}
                    disabled={!canRefreshAuthorityStatus || isBusy}
                  >
                    {pendingAction === 'refreshAuthority' ? 'Refreshing…' : 'Refresh MOS status'}
                  </Button>
                  <Button
                    data-testid="enrollment-approve-secret-release"
                    variant="outline"
                    onClick={onApproveSecretRelease}
                    disabled={!canApproveSecretRelease || isBusy}
                  >
                    {pendingAction === 'approveSecretRelease'
                      ? 'Approving secret…'
                      : 'Approve secret release'}
                  </Button>
                  <Button
                    data-testid="enrollment-approve-activation"
                    variant="outline"
                    onClick={onApproveActivation}
                    disabled={!canApproveActivation || isBusy}
                  >
                    {pendingAction === 'approveActivation'
                      ? 'Approving activation…'
                      : 'Approve activation'}
                  </Button>
                </div>
                <p className="mt-3 text-sm text-text-secondary">
                  Issue token stays blocked until MOS client provisioning, secret release, and final
                  activation approval are complete.
                </p>
              </>
            ) : (
              <p className="text-sm text-text-secondary">
                Prepare and approve a manifest before requesting MOS authority provisioning.
              </p>
            )}
          </div>

          <div className="rounded-xl border border-border bg-surface p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium">
              <KeyRound className="h-4 w-4 text-primary" />
              Health & federation checks
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                data-testid="enrollment-doctor-check"
                variant="outline"
                onClick={onRunDoctorCheck}
                disabled={!canRunDoctorCheck || isBusy}
              >
                {pendingAction === 'doctorCheck' ? 'Running doctor check…' : 'Run doctor check'}
              </Button>
              <Button
                data-testid="enrollment-federation-check"
                variant="outline"
                onClick={onRunFederationCheck}
                disabled={!canRunFederationCheck || isBusy}
              >
                {pendingAction === 'federationCheck'
                  ? 'Running federation check…'
                  : 'Run federation check'}
              </Button>
            </div>
            {doctorCheck || federationCheck ? (
              <div className="mt-3 grid gap-2 text-sm">
                {doctorCheck ? (
                  <>
                    <div>
                      <span className="text-text-secondary">Doctor evidence enrollment:</span>{' '}
                      <span className="font-mono text-xs">{doctorCheck.enrollmentId}</span>
                    </div>
                    <div>
                      <span className="text-text-secondary">Device:</span>{' '}
                      <span className="font-mono text-xs">{doctorCheck.deviceId}</span>
                    </div>
                    <div>
                      <span className="text-text-secondary">Federation key:</span>{' '}
                      <span className="font-mono text-xs">{doctorCheck.federationKeyId}</span>
                    </div>
                    <div>
                      <span className="text-text-secondary">Cloud session:</span>{' '}
                      <span className="font-mono text-xs">
                        {doctorCheck.cloudSessionId ?? 'not available'}
                      </span>
                    </div>
                  </>
                ) : null}
                {federationCheck ? (
                  <>
                    <div>
                      <span className="text-text-secondary">Federation evidence enrollment:</span>{' '}
                      <span className="font-mono text-xs">{federationCheck.enrollmentId}</span>
                    </div>
                    <div>
                      <span className="text-text-secondary">Device:</span>{' '}
                      <span className="font-mono text-xs">{federationCheck.deviceId}</span>
                    </div>
                    <div>
                      <span className="text-text-secondary">Federation key:</span>{' '}
                      <span className="font-mono text-xs">{federationCheck.federationKeyId}</span>
                    </div>
                    <div>
                      <span className="text-text-secondary">Cloud session:</span>{' '}
                      <span className="font-mono text-xs">
                        {federationCheck.cloudSessionId ?? 'not available'}
                      </span>
                    </div>
                  </>
                ) : null}
              </div>
            ) : null}
            {prepared ? (
              <p className="mt-3 text-sm text-text-secondary">
                These checks return session-binding evidence needed for federation debug and can be
                rerun at any time.
              </p>
            ) : null}
          </div>

          <div className="rounded-xl border border-border bg-surface p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium">
              <KeyRound className="h-4 w-4 text-primary" />
              Prepared enrollment
            </div>
            {prepared ? (
              <dl className="grid gap-2 text-sm">
                <div>
                  <dt className="text-text-secondary">Enrollment ID</dt>
                  <dd>{prepared.enrollment.id}</dd>
                </div>
                <div>
                  <dt className="text-text-secondary">Status</dt>
                  <dd data-testid="enrollment-status">{prepared.enrollment.status}</dd>
                </div>
                <div>
                  <dt className="text-text-secondary">Manifest hash</dt>
                  <dd className="break-all font-mono text-xs">{prepared.manifestHash}</dd>
                </div>
                <div>
                  <dt className="text-text-secondary">Auto-remediations</dt>
                  <dd>{prepared.autoRemediations.length}</dd>
                </div>
              </dl>
            ) : (
              <p className="text-sm text-text-secondary">
                No prepared enrollment yet. Start by preparing a manifest for the target machine.
              </p>
            )}
          </div>
        </section>

        <section className="space-y-4">
          <div className="rounded-xl border border-border bg-surface p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium">
              <TerminalSquare className="h-4 w-4 text-primary" />
              Enrollment command
            </div>
            {issued ? (
              <>
                <p className="mb-3 text-sm text-text-secondary">
                  Run this command on{' '}
                  <span className="font-medium text-text-primary">{issued.deviceId}</span>. It
                  redeems the token, materializes the bootstrap runtime, and reports progress.
                </p>
                <pre className="overflow-x-auto rounded-lg border border-border bg-background p-3 text-xs text-text-primary">
                  <code data-testid="enrollment-command">{issued.command}</code>
                </pre>
                <div className="mt-3 grid gap-2 text-sm">
                  <div>
                    <span className="text-text-secondary">Enrollment ID:</span>{' '}
                    {issued.enrollmentId}
                  </div>
                  <div>
                    <span className="text-text-secondary">Expires at:</span> {issued.expiresAt}
                  </div>
                </div>
              </>
            ) : (
              <p className="text-sm text-text-secondary">
                No token issued yet. Approve the manifest, then issue a tokenized command.
              </p>
            )}
          </div>
          {launch ? (
            <div className="rounded-xl border border-border bg-surface p-4">
              <div className="mb-2 flex items-center gap-2 text-sm font-medium">
                <TerminalSquare className="h-4 w-4 text-primary" />
                Local deployment command
              </div>
              <p className="mb-3 text-sm text-text-secondary">
                Use this command to start the subordinate on this workstation.
              </p>
              <pre className="overflow-x-auto rounded-lg border border-border bg-background p-3 text-xs text-text-primary">
                <code data-testid="enrollment-launch-command">{launch.command}</code>
              </pre>
              <dl className="mt-3 grid gap-2 text-sm">
                <div>
                  <dt className="text-text-secondary">Compose file</dt>
                  <dd className="break-all font-mono text-xs">{launch.composeFile}</dd>
                </div>
                <div>
                  <dt className="text-text-secondary">Image</dt>
                  <dd className="break-all font-mono text-xs">{launch.image}</dd>
                </div>
                <div>
                  <dt className="text-text-secondary">Readiness</dt>
                  <dd>
                    status={launch.readiness.status} ok={launch.readiness.ok ? 'true' : 'false'}
                    {launch.readiness.phase ? ` phase=${launch.readiness.phase}` : ''}
                  </dd>
                </div>
                <div>
                  <dt className="text-text-secondary">Provider auth</dt>
                  <dd className="text-xs">
                    {formatProviderAuthStatus(launch.readiness.providerAuth)}
                  </dd>
                </div>
              </dl>
            </div>
          ) : null}

          {prepared ? (
            <div className="rounded-xl border border-border bg-surface p-4">
              <div className="mb-2 text-sm font-medium">Manifest summary</div>
              <pre className="whitespace-pre-wrap text-xs text-text-secondary">
                <code>{prepared.summaryMarkdown}</code>
              </pre>
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}

function toErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function formatEnrollmentCreatedAt(value: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

// Admin-only browse panel above the prepare wizard. Revoke is authoritative in
// MOS (Telegram `/maestro revoke <id>`), so the row surfaces that command rather
// than a Maestro-side revoke API (out of scope for the enrollment-management B2 half).
export function LocalEnrollmentListPanel(): React.ReactElement | null {
  const { data: session } = useQuery({
    queryKey: ['authSession'],
    queryFn: getAuthSession,
  });
  const isAdmin = session?.operator?.role === 'admin';
  const {
    data: enrollments,
    refetch,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ['localEnrollments'],
    queryFn: listLocalEnrollments,
    enabled: isAdmin,
  });

  if (!isAdmin) return null;

  return (
    <div className="border-b border-border px-6 py-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium">
          <ListChecks className="h-4 w-4 text-primary" />
          Enrollments
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            void refetch();
          }}
          disabled={isFetching}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>
      {isError ? (
        <p className="text-sm text-error">Failed to load enrollments.</p>
      ) : !enrollments || enrollments.length === 0 ? (
        <p className="text-sm text-text-secondary">No local enrollments yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-text-tertiary">
              <tr>
                <th className="py-1 pr-4 font-medium">Device</th>
                <th className="py-1 pr-4 font-medium">Status</th>
                <th className="py-1 pr-4 font-medium">Created</th>
                <th className="py-1 pr-4 font-medium">Revoke</th>
              </tr>
            </thead>
            <tbody>
              {enrollments.map(item => (
                <tr key={item.id} className="border-t border-border/60">
                  <td className="py-1.5 pr-4">
                    <div>{item.deviceId}</div>
                    <div className="text-xs text-text-tertiary">{item.id}</div>
                  </td>
                  <td className="py-1.5 pr-4">{item.status}</td>
                  <td className="py-1.5 pr-4 text-text-secondary">
                    {formatEnrollmentCreatedAt(item.createdAt)}
                  </td>
                  <td className="py-1.5 pr-4">
                    <code
                      className="rounded bg-surface-elevated px-1.5 py-0.5 text-xs text-text-secondary"
                      title="Send this command to the MOS Telegram bot to revoke this enrollment"
                    >
                      /maestro revoke {item.id}
                    </code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Admin-only surface where the operator approves a device-authorization request
// that the `paired:enroll` CLI started. The CLI prints a userCode + a link here;
// the operator types the code and approves, and the server issues + hands the
// token back to the polling CLI. Nothing redeemable is shown in the browser.
export function EnrollmentDeviceApprovePanel(): React.ReactElement | null {
  const { data: session } = useQuery({
    queryKey: ['authSession'],
    queryFn: getAuthSession,
  });
  const [userCode, setUserCode] = useState('');
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const approveMutation = useMutation({
    mutationFn: (code: string) => approveEnrollmentDeviceAuth(code),
    onMutate: () => {
      setStatusMessage(null);
      setErrorMessage(null);
    },
    onSuccess: () => {
      setStatusMessage('Device approved — the enrolling machine will receive its token.');
      setUserCode('');
    },
    onError: error => {
      setErrorMessage(error instanceof Error ? error.message : String(error));
    },
  });

  if (session?.operator?.role !== 'admin') return null;

  const trimmed = userCode.trim();
  const isBusy = approveMutation.isPending;

  return (
    <div className="border-b border-border px-6 py-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-medium">
        <Smartphone className="h-4 w-4 text-primary" />
        Approve device sign-in
      </div>
      <p className="mb-3 text-sm text-text-secondary">
        Enter the code shown by <code>paired:enroll</code> on the enrolling machine to issue its
        enrollment token.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={userCode}
          onChange={event => {
            setUserCode(event.target.value);
          }}
          placeholder="XXXX-XXXX"
          spellCheck={false}
          autoCapitalize="characters"
          className="h-10 w-48 rounded-md border border-border bg-surface px-3 font-mono text-sm uppercase text-text-primary"
        />
        <Button
          onClick={() => {
            approveMutation.mutate(trimmed);
          }}
          disabled={isBusy || trimmed.length === 0}
        >
          {isBusy ? 'Approving…' : 'Approve device sign-in'}
        </Button>
      </div>
      {statusMessage && <p className="mt-2 text-sm text-success">{statusMessage}</p>}
      {errorMessage && <p className="mt-2 text-sm text-error">{errorMessage}</p>}
    </div>
  );
}

export function LocalEnrollmentPage(): React.ReactElement {
  const initialState = createInitialLocalEnrollmentPageState();
  const [form, setForm] = useState<PrepareLocalEnrollmentInput>(initialState.form);
  const [prepared, setPrepared] = useState<PrepareLocalEnrollmentResult | null>(null);
  const [authority, setAuthority] = useState<LocalEnrollmentAuthorityState | null>(null);
  const [issued, setIssued] = useState<LocalEnrollmentTokenResult | null>(null);
  const [launch, setLaunch] = useState<LocalEnrollmentDockerLaunchResult | null>(null);
  const [doctorCheck, setDoctorCheck] = useState<LocalEnrollmentDoctorCheckResult | null>(null);
  const [federationCheck, setFederationCheck] =
    useState<LocalEnrollmentFederationCheckResult | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<
    | 'prepare'
    | 'approve'
    | 'requestAuthority'
    | 'refreshAuthority'
    | 'approveSecretRelease'
    | 'approveActivation'
    | 'issue'
    | 'launchDocker'
    | 'doctorCheck'
    | 'federationCheck'
    | 'reissue'
    | null
  >(null);

  const prepareMutation = useMutation({
    mutationFn: prepareLocalEnrollment,
    onMutate: () => {
      setPendingAction('prepare');
      setStatusMessage(null);
      setErrorMessage(null);
    },
    onSuccess: result => {
      setPrepared(result);
      setAuthority(null);
      setIssued(null);
      setLaunch(null);
      setDoctorCheck(null);
      setFederationCheck(null);
      setStatusMessage(
        `Prepared ${result.enrollment.id}. Review the manifest and approve it to continue.`
      );
    },
    onError: error => {
      setErrorMessage(toErrorMessage(error));
    },
    onSettled: () => {
      setPendingAction(null);
    },
  });

  const approveMutation = useMutation({
    mutationFn: ({ id, manifestHash }: { id: string; manifestHash: string }) =>
      approveLocalEnrollment(id, manifestHash),
    onMutate: () => {
      setPendingAction('approve');
      setStatusMessage(null);
      setErrorMessage(null);
    },
    onSuccess: result => {
      setPrepared(current =>
        current
          ? {
              ...current,
              enrollment: result.enrollment,
              manifestHash: result.enrollment.manifestHash,
            }
          : current
      );
      setLaunch(null);
      setStatusMessage(getManifestApprovalSuccessMessage(result.enrollment.id));
    },
    onError: error => {
      setErrorMessage(toErrorMessage(error));
    },
    onSettled: () => {
      setPendingAction(null);
    },
  });

  const requestAuthorityMutation = useMutation({
    mutationFn: (id: string) => requestLocalEnrollmentAuthorityProvisioning(id),
    onMutate: () => {
      setPendingAction('requestAuthority');
      setStatusMessage(null);
      setErrorMessage(null);
    },
    onSuccess: result => {
      setAuthority(result.authority);
      setLaunch(null);
      setStatusMessage(getAuthorityProvisioningSuccessMessage(result.authority.enrollmentId));
    },
    onError: error => {
      setErrorMessage(toErrorMessage(error));
    },
    onSettled: () => {
      setPendingAction(null);
    },
  });

  const refreshAuthorityMutation = useMutation({
    mutationFn: (id: string) => fetchLocalEnrollmentAuthorityStatus(id),
    onMutate: () => {
      setPendingAction('refreshAuthority');
      setStatusMessage(null);
      setErrorMessage(null);
    },
    onSuccess: result => {
      setAuthority(result.authority);
      setLaunch(null);
      setStatusMessage(getAuthorityRefreshSuccessMessage(result.authority.enrollmentId));
    },
    onError: error => {
      setErrorMessage(toErrorMessage(error));
    },
    onSettled: () => {
      setPendingAction(null);
    },
  });

  const approveActivationMutation = useMutation({
    mutationFn: (id: string) => approveLocalEnrollmentActivation(id),
    onMutate: () => {
      setPendingAction('approveActivation');
      setStatusMessage(null);
      setErrorMessage(null);
    },
    onSuccess: result => {
      setAuthority(result.authority);
      setLaunch(null);
      setStatusMessage(getActivationApprovalSuccessMessage(result.authority.enrollmentId));
    },
    onError: error => {
      setErrorMessage(toErrorMessage(error));
    },
    onSettled: () => {
      setPendingAction(null);
    },
  });

  const approveSecretReleaseMutation = useMutation({
    mutationFn: (id: string) => approveLocalEnrollmentSecretRelease(id),
    onMutate: () => {
      setPendingAction('approveSecretRelease');
      setStatusMessage(null);
      setErrorMessage(null);
    },
    onSuccess: result => {
      setAuthority(result.authority);
      setLaunch(null);
      setStatusMessage(getSecretReleaseApprovalSuccessMessage(result.authority.enrollmentId));
    },
    onError: error => {
      setErrorMessage(toErrorMessage(error));
    },
    onSettled: () => {
      setPendingAction(null);
    },
  });

  const issueMutation = useMutation({
    mutationFn: ({ id, reissue }: { id: string; reissue: boolean }) =>
      reissue ? reissueLocalEnrollmentToken(id) : issueLocalEnrollmentToken(id),
    onMutate: ({ reissue }) => {
      setPendingAction(reissue ? 'reissue' : 'issue');
      setStatusMessage(null);
      setErrorMessage(null);
    },
    onSuccess: result => {
      setIssued(result);
      setPrepared(current =>
        current
          ? {
              ...current,
              enrollment: {
                ...current.enrollment,
                status: 'token_issued',
              },
            }
          : current
      );
      setLaunch(null);
      setStatusMessage(`Issued enrollment command for ${result.deviceId}.`);
    },
    onError: error => {
      setErrorMessage(toErrorMessage(error));
    },
    onSettled: () => {
      setPendingAction(null);
    },
  });

  const launchMutation = useMutation({
    mutationFn: ({ id, token }: { id: string; token: string }) =>
      launchLocalEnrollmentDocker(id, { token }),
    onMutate: () => {
      setPendingAction('launchDocker');
      setStatusMessage(null);
      setErrorMessage(null);
    },
    onSuccess: result => {
      setLaunch(result);
      setStatusMessage(`Started local deployment for ${result.deviceId}.`);
    },
    onError: error => {
      setErrorMessage(toErrorMessage(error));
    },
    onSettled: () => {
      setPendingAction(null);
    },
  });

  const doctorCheckMutation = useMutation({
    mutationFn: (id: string) => runLocalEnrollmentDoctorCheck(id),
    onMutate: () => {
      setPendingAction('doctorCheck');
      setStatusMessage(null);
      setErrorMessage(null);
      setDoctorCheck(null);
    },
    onSuccess: result => {
      setDoctorCheck(result);
      setStatusMessage(`Doctor check complete for ${result.enrollmentId}.`);
    },
    onError: error => {
      setErrorMessage(toErrorMessage(error));
    },
    onSettled: () => {
      setPendingAction(null);
    },
  });

  const federationCheckMutation = useMutation({
    mutationFn: (id: string) => runLocalEnrollmentFederationCheck(id),
    onMutate: () => {
      setPendingAction('federationCheck');
      setStatusMessage(null);
      setErrorMessage(null);
      setFederationCheck(null);
    },
    onSuccess: result => {
      setFederationCheck(result);
      setStatusMessage(`Federation check complete for ${result.enrollmentId}.`);
    },
    onError: error => {
      setErrorMessage(toErrorMessage(error));
    },
    onSettled: () => {
      setPendingAction(null);
    },
  });

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <LocalEnrollmentListPanel />
      <EnrollmentDeviceApprovePanel />
      <div className="min-h-0 flex-1">
        <LocalEnrollmentView
          form={form}
          onFormChange={(field, value) => {
            setForm(current => ({
              ...current,
              [field]: value,
            }));
          }}
          onPrepare={() => {
            prepareMutation.mutate(form);
          }}
          onApprove={() => {
            if (!prepared) return;
            approveMutation.mutate({
              id: prepared.enrollment.id,
              manifestHash: prepared.manifestHash,
            });
          }}
          onRequestAuthorityProvisioning={() => {
            if (!prepared) return;
            requestAuthorityMutation.mutate(prepared.enrollment.id);
          }}
          onRefreshAuthorityStatus={() => {
            if (!prepared) return;
            refreshAuthorityMutation.mutate(prepared.enrollment.id);
          }}
          onApproveSecretRelease={() => {
            if (!prepared) return;
            approveSecretReleaseMutation.mutate(prepared.enrollment.id);
          }}
          onApproveActivation={() => {
            if (!prepared) return;
            approveActivationMutation.mutate(prepared.enrollment.id);
          }}
          onIssue={() => {
            if (!prepared) return;
            issueMutation.mutate({ id: prepared.enrollment.id, reissue: false });
          }}
          onLaunch={() => {
            if (!prepared || !issued) return;
            if (issued.enrollmentId !== prepared.enrollment.id) return;
            launchMutation.mutate({ id: prepared.enrollment.id, token: issued.token });
          }}
          onRunDoctorCheck={() => {
            if (!prepared) return;
            doctorCheckMutation.mutate(prepared.enrollment.id);
          }}
          onRunFederationCheck={() => {
            if (!prepared) return;
            federationCheckMutation.mutate(prepared.enrollment.id);
          }}
          onReissue={() => {
            const enrollmentId = issued?.enrollmentId ?? prepared?.enrollment.id;
            if (!enrollmentId) return;
            issueMutation.mutate({ id: enrollmentId, reissue: true });
          }}
          prepared={prepared}
          authority={authority}
          issued={issued}
          launch={launch}
          doctorCheck={doctorCheck}
          federationCheck={federationCheck}
          statusMessage={statusMessage}
          errorMessage={errorMessage}
          pendingAction={pendingAction}
        />
      </div>
    </div>
  );
}
